/**
 * The journeys' sections — one definition shared by the app sidebar
 * (which lists a journey's sections under its entry while that journey is the
 * current page) and the journey pages (which render the sections as tab
 * contents and publish each section's live count).
 *
 * Navigation is URL-driven: <journey path>?tab=<section>.
 */
import { useEffect, type ComponentType } from 'react'
import { BarChart3, Fingerprint, Goal, Grid3x3, NotebookPen, Scale, Search, Spotlight, Users } from 'lucide-react'
import { create } from 'zustand'

export type JourneyId = 'employee' | 'hub' | 'dashboards'

export interface JourneySection {
  label: string
  icon: ComponentType<{ className?: string }>
}

export interface Journey {
  id: JourneyId
  /** The sidebar entry the sections sit under. */
  title: string
  path: string
  defaultSection: string
  groups: { label: string; items: string[] }[]
  sections: Record<string, JourneySection>
}

export const JOURNEYS: Record<JourneyId, Journey> = {
  employee: {
    id: 'employee',
    title: 'Team Member Journey',
    path: '/employee-journey',
    defaultSection: 'objectives',
    groups: [
      { label: 'My work', items: ['objectives', 'portfolio'] },
      { label: 'Conversations', items: ['planning', 'feedback'] },
      { label: 'Growth', items: ['development'] },
    ],
    sections: {
      objectives: { label: 'my Objectives', icon: Goal },
      portfolio: { label: 'my Impact Portfolio', icon: NotebookPen },
      planning: { label: 'my Check-Ins', icon: Spotlight },
      feedback: { label: 'my "In-the-Moment" Feedback', icon: Users },
      development: { label: 'my Personalised Growth', icon: Fingerprint },
    },
  },
  hub: {
    id: 'hub',
    title: 'People Leader Hub',
    path: '/people-leader-hub',
    defaultSection: 'overview',
    groups: [
      { label: 'My Team', items: ['overview', 'objectives', 'reviews', 'development'] },
      { label: 'Cycles', items: ['calibration', 'talent'] },
      { label: 'Insights', items: ['analytics'] },
    ],
    sections: {
      overview: { label: 'my Team "at-a-glance"', icon: Search },
      objectives: { label: 'Configuring Objectives', icon: Goal },
      reviews: { label: '1:1 Check-In Dialogues', icon: Spotlight },
      calibration: { label: 'Calibration', icon: Scale },
      talent: { label: 'Annual I&G Review', icon: Grid3x3 },
      development: { label: "my Team's Growth Plans", icon: Fingerprint },
      analytics: { label: 'Team Analytics', icon: BarChart3 },
    },
  },
  // Dashboards (HR, 9 Oct 2026: "can't we just call it dashboards"): the Workforce
  // Explorer and the Annual I&G Review's talent dashboard, each section shown only
  // to the people who may see it (the page hides the rest).
  dashboards: {
    id: 'dashboards',
    title: 'Dashboards',
    path: '/workforce-explorer',
    defaultSection: 'workforce',
    groups: [{ label: 'Reporting', items: ['workforce', 'talent'] }],
    sections: {
      workforce: { label: 'Workforce', icon: BarChart3 },
      talent: { label: 'Talent review', icon: Grid3x3 },
    },
  },
}

/** The journey whose page a pathname belongs to, if any. */
export function journeyForPath(pathname: string): Journey | undefined {
  return Object.values(JOURNEYS).find((j) => j.path === pathname)
}

/** The section a location's search string names, else the journey's default. */
export function sectionFromSearch(journey: Journey, search: string): string {
  const tab = new URLSearchParams(search).get('tab')
  return tab && tab in journey.sections ? tab : journey.defaultSection
}

// ---------------------------------------------------------------------------
// What a page knows that the definition cannot: live counts, sections hidden
// in the page's current mode, and a renamed section. Published while the page
// is mounted, read by the sidebar.
// ---------------------------------------------------------------------------
export interface JourneyNavDetails {
  /** A count line under the section name, e.g. "2 pending". */
  subtitles: Record<string, string>
  /** Sections the page cannot show right now (e.g. a legacy year). */
  hidden: string[]
  /** A different name for a section in the page's current mode. */
  labels: Record<string, string>
}

const EMPTY_DETAILS: JourneyNavDetails = { subtitles: {}, hidden: [], labels: {} }

/**
 * The Team Member Journey's sections hidden for a year and a person. A legacy
 * (F&O) year has only its history. Someone outside the review cycle (an ICA,
 * casual staff, a learner, or excluded by HR) has no objectives or check-ins:
 * an objective would wait for an approval nobody sees, and a check-in cannot be
 * held without approved objectives. When /auth/me doesn't say, everything shows.
 */
export function hiddenEmployeeSections(legacyYear: boolean, inReviewCycle: boolean | undefined): string[] {
  if (legacyYear) return ['portfolio', 'planning', 'feedback', 'development']
  if (inReviewCycle === false) return ['objectives', 'planning']
  return []
}

interface JourneyNavState {
  details: Record<JourneyId, JourneyNavDetails>
  publish: (journey: JourneyId, details: Partial<JourneyNavDetails>) => void
  clear: (journey: JourneyId) => void
}

export const useJourneyNav = create<JourneyNavState>((set) => ({
  details: { employee: EMPTY_DETAILS, hub: EMPTY_DETAILS, dashboards: EMPTY_DETAILS },
  publish: (journey, details) =>
    set((state) => ({ details: { ...state.details, [journey]: { ...EMPTY_DETAILS, ...details } } })),
  clear: (journey) => set((state) => ({ details: { ...state.details, [journey]: EMPTY_DETAILS } })),
}))

/**
 * Publishes a page's section details to the sidebar for as long as the page
 * is mounted. Render it anywhere in the page's tree; it draws nothing.
 */
export function JourneyNavPublisher({ journey, ...details }: { journey: JourneyId } & Partial<JourneyNavDetails>) {
  const publish = useJourneyNav((state) => state.publish)
  const clear = useJourneyNav((state) => state.clear)
  const key = JSON.stringify(details)
  useEffect(() => {
    publish(journey, JSON.parse(key))
    return () => clear(journey)
  }, [journey, key, publish, clear])
  return null
}
